/**
 * Routage minimal.
 *
 * Module séparé de `App.tsx` pour une raison précise : la page d'accueil a besoin de
 * naviguer, et `App.tsx` importe la page d'accueil. Importer `navigate` depuis `App.tsx`
 * créerait un cycle. Un troisième module commun supprime le problème.
 */

import { useEffect, useState } from 'react';

export type Route = { name: 'home' } | { name: 'room'; roomId: string };

/** Chemin des salles de partage : `/t/<identifiant>`. */
const ROOM_PATTERN = /^\/t\/([A-Za-z0-9]{8,64})\/?$/;

export function parseRoute(pathname: string): Route {
  const match = ROOM_PATTERN.exec(pathname);
  const roomId = match?.[1];
  if (roomId) return { name: 'room', roomId };
  return { name: 'home' };
}

/**
 * Navigue sans recharger la page.
 *
 * `pushState` ne déclenche pas `popstate` : on l'émet nous-mêmes pour que le routeur
 * réagisse à une navigation programmatique.
 */
export function navigate(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Route courante, mise à jour sur navigation et sur retour arrière du navigateur. */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const update = (): void => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);

  return route;
}
